import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiArrowDown, PiSpinner } from 'react-icons/pi';
import './style.css';

const PULL_THRESHOLD = 72;
const MAX_PULL = 100;

const PullToRefresh = ({ onRefresh, children }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startYRef = useRef(null);
  const trackingRef = useRef(false);

  const handleTouchStart = (event) => {
    if (isRefreshing) return;

    /*
     * Só permite pull-to-refresh quando a página
     * realmente está no topo.
     */
    if (window.scrollY > 0) return;

    startYRef.current = event.touches[0].clientY;
    trackingRef.current = true;
  };

  const handleTouchMove = (event) => {
    if (
      !trackingRef.current ||
      isRefreshing ||
      startYRef.current === null
    ) {
      return;
    }

    if (window.scrollY > 0) {
      trackingRef.current = false;
      setPullDistance(0);
      return;
    }

    const currentY = event.touches[0].clientY;
    const rawDistance = currentY - startYRef.current;

    if (rawDistance <= 0) {
      setPullDistance(0);
      return;
    }

    /*
     * Resistência no gesto para não deslocar
     * o conteúdo 1:1 com o dedo.
     */
    const resistedDistance = Math.min(
      rawDistance * 0.55,
      MAX_PULL
    );

    setPullDistance(resistedDistance);
  };

  const handleTouchEnd = async () => {
    if (!trackingRef.current) return;

    trackingRef.current = false;
    startYRef.current = null;

    const shouldRefresh =
      pullDistance >= PULL_THRESHOLD;

    if (!shouldRefresh) {
      setPullDistance(0);
      return;
    }

    setIsRefreshing(true);
    setPullDistance(PULL_THRESHOLD);

    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  };

  const pullProgress = Math.min(
    pullDistance / PULL_THRESHOLD,
    1
  );

  const readyToRefresh =
    pullDistance >= PULL_THRESHOLD;

  return (
    <div
      className={`pull-to-refresh ${isRefreshing ? 'pull-to-refresh--refreshing' : ''
        }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{
        '--pull-distance': `${pullDistance}px`,
        '--pull-progress': pullProgress,
      }}
    >
      <div
        className="pull-to-refresh__indicator"
        role="status"
        aria-live="polite"
        aria-hidden={!isRefreshing && pullDistance === 0}
      >
        <span className="pull-to-refresh__icon">
          {isRefreshing ? (
            <PiSpinner className="pull-to-refresh__spinner" />
          ) : (
            <PiArrowDown
              className={
                readyToRefresh
                  ? 'pull-to-refresh__arrow pull-to-refresh__arrow--ready'
                  : 'pull-to-refresh__arrow'
              }
            />
          )}
        </span>

        <span className="pull-to-refresh__label">
          {isRefreshing
            ? 'Atualizando...'
            : readyToRefresh
              ? 'Solte para atualizar'
              : 'Puxe para atualizar'}
        </span>
      </div>

      <div className="pull-to-refresh__content">
        {children}
      </div>
    </div>
  );
};

PullToRefresh.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default PullToRefresh;